import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ArrowDown, ArrowUp, ChevronDown, GripVertical, X } from 'lucide-react';
import * as React from 'react';

import type { IDataGroupLevel } from '@/design';
import type {
  IListAggregates,
  IListField,
  IListOrder,
  IListRow
} from '../../api/lists-contracts';
import { formatListValue } from '../../lists';

/**
 * A list's grouping (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.7) — Conga's "Drag a column header here to group by it" strip, the demo's (SR-221, R3
 * reversed) made live. A level is a field; levels nest, the outermost first, at most five (the
 * wire's bound); each chip its order (a press reverses it), its column, "(n groups)" and ✕. **A
 * header is dragged onto the strip**, and **Group by ▾** / **Then by ▾** offer the groupable
 * columns shown from the keyboard — a drag alone would leave grouping to a mouse.
 *
 * **The grouping is the view's `groupBy`**, saved with it: a change marks the view changed and
 * reads the rows again, and every reader regroups a shared view — only saving over it is
 * `lists.share`'s. What is folded is the screen's.
 *
 * **The rows are the page's, the counts and sums the whole result's** (survey-api's §15.6): a
 * heading is matched to `/aggregates`' `groups` by its keys — each level's value **as the row holds
 * it**, which is what a row is grouped by here (`groupKey`), so a heading and its count cannot
 * disagree on a label. A chip's "(n groups)" counts the level's groups there too; nothing here
 * counts a result it was not sent.
 */

/** The drag's payload type — a header carries its field's code under it, and nothing else does. */
export const GROUP_DRAG_TYPE = 'application/x-list-field';

/** survey-api's bound on `groupBy` (`ListQueryBinder`). */
export const MAX_GROUP_LEVELS = 5;

/** A value as a group's key — the stored value as JSON, `null` for a row holding none. */
export function groupKey(value: unknown): string {
  return JSON.stringify(value ?? null);
}

/** What a header's drag carries, where its field groups. */
export function dragProps(field: IListField): React.HTMLAttributes<HTMLElement> {
  if (!field.groupable) return {};
  return {
    draggable: true,
    onDragStart: (event) => {
      event.dataTransfer.setData(GROUP_DRAG_TYPE, field.code);
      event.dataTransfer.effectAllowed = 'copy';
    }
  };
}

/**
 * The grouping's levels as the table takes them. A group's key is the stored value; its heading
 * says the value by its field's kind, "(Blank)" for none. The groups keep the order the rows came
 * in — survey-api sorts by the grouping first — so the table's own order is the order of first
 * appearance.
 */
export function groupLevels(
  groupBy: readonly IListOrder[],
  fieldOf: (code: string) => IListField | undefined,
  rows: readonly IListRow[],
  personOf?: (accountId: string) => string | undefined
): IDataGroupLevel<IListRow>[] {
  return groupBy.map(({ field: code }) => {
    const field = fieldOf(code);
    const first = new Map<string, number>();
    // A person's heading is named as its rows name the account: survey-api's label on the cell, else
    // the account as the assignees read knows it — "an account since removed" only where neither does.
    const labels = new Map<string, Nullable<string>>();
    rows.forEach((row, index) => {
      const cell = row.cells[code];
      const key = groupKey(cell?.v);
      if (!first.has(key)) first.set(key, index);
      if (!labels.get(key) && cell?.label) labels.set(key, cell.label);
    });
    return {
      id: code,
      label: field?.label ?? code,
      value: (row) => groupKey(row.cells[code]?.v),
      compare: (left, right) => (first.get(left) ?? 0) - (first.get(right) ?? 0),
      display: (key) => {
        const value: unknown = JSON.parse(key);
        if (value === null) return '(Blank)';
        const label =
          labels.get(key) ?? (field?.kind === 'person' && typeof value === 'string' ? personOf?.(value) : undefined);
        const said = field ? formatListValue(field, value, label ?? null) : String(value);
        return said === '' ? '(Blank)' : said;
      }
    };
  });
}

/** The aggregate group a heading stands for — its levels' keys, outermost first. */
export function aggregateGroupOf(
  aggregates: Nullable<IListAggregates>,
  path: readonly string[]
): IListAggregates['groups'][number] | undefined {
  return aggregates?.groups.find(
    (group) =>
      group.keys.length === path.length &&
      group.keys.every((key, index) => groupKey(key) === path[index])
  );
}

export function GroupStrip({
  groupBy,
  columns,
  fieldOf,
  aggregates,
  onChange,
  hint = 'Drag a column header here to group by it'
}: {
  groupBy: readonly IListOrder[];
  /** The view's columns — what Group by ▾ offers, where they group. */
  columns: readonly string[];
  fieldOf: (code: string) => IListField | undefined;
  aggregates: Nullable<IListAggregates>;
  onChange: (groupBy: IListOrder[]) => void;
  /** What the empty strip says — a report's builder, with no header to drag, says Group by ▾. */
  hint?: string;
}) {
  const [over, setOver] = React.useState(false);
  const used = new Set(groupBy.map((level) => level.field));
  const full = groupBy.length >= MAX_GROUP_LEVELS;
  const offered = columns
    .map(fieldOf)
    .filter((field): field is IListField => field !== undefined && field.groupable && !used.has(field.code));
  const add = (code: string) => {
    if (full || used.has(code) || !fieldOf(code)?.groupable) return;
    onChange([...groupBy, { field: code, dir: 'asc' }]);
  };
  const carries = (event: React.DragEvent) => event.dataTransfer.types.includes(GROUP_DRAG_TYPE);
  const groupsAt = (depth: number): Nullable<number> =>
    aggregates ? aggregates.groups.filter((group) => group.keys.length === depth + 1).length : null;

  return (
    <div
      role="group"
      aria-label="Grouping"
      data-group-strip=""
      onDragOver={(event) => {
        if (!carries(event) || full) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        setOver(false);
        if (!carries(event)) return;
        event.preventDefault();
        add(event.dataTransfer.getData(GROUP_DRAG_TYPE));
      }}
      className={cn(
        'flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-[0.8125rem]',
        over && 'border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-teal)_6%,white)]'
      )}
    >
      {groupBy.length === 0 && (
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <GripVertical className="size-4" aria-hidden />
          {hint}
        </span>
      )}
      {groupBy.map((level, index) => (
        <GroupChip
          key={level.field}
          label={fieldOf(level.field)?.label ?? level.field}
          code={level.field}
          descending={level.dir === 'desc'}
          groups={groupsAt(index)}
          onReverse={() =>
            onChange(
              groupBy.map((entry, at) =>
                at === index ? { ...entry, dir: entry.dir === 'desc' ? 'asc' : 'desc' } : entry
              )
            )
          }
          onRemove={() => onChange(groupBy.filter((_, at) => at !== index))}
        />
      ))}
      <span className="flex-1" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            disabled={offered.length === 0 || full}
            className="h-7 gap-1 px-2 font-normal"
          >
            {groupBy.length === 0 ? 'Group by' : 'Then by'}
            <ChevronDown className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-80 w-64 overflow-y-auto">
          {offered.map((field) => (
            <DropdownMenuItem key={field.code} onSelect={() => add(field.code)}>
              {field.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function GroupChip({
  label,
  code,
  descending,
  groups,
  onReverse,
  onRemove
}: {
  label: string;
  code: string;
  descending: boolean;
  /** How many groups the level makes over the whole result, or null while unknown. */
  groups: Nullable<number>;
  onReverse: () => void;
  onRemove: () => void;
}) {
  const Arrow = descending ? ArrowDown : ArrowUp;
  return (
    <span
      data-group-level={code}
      className="inline-flex h-7 items-center gap-0.5 rounded-full border border-border bg-[var(--color-off-white)] pr-1 pl-1"
    >
      <button
        type="button"
        aria-label={`${label} groups ${descending ? 'descending' : 'ascending'} — reverse`}
        onClick={onReverse}
        className="rounded-full p-0.5 text-[var(--veritant-royal-70)] outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Arrow className="size-3.5" aria-hidden />
      </button>
      <span className="px-0.5 font-medium">
        {label}
        {groups !== null && (
          <span className="font-normal tabular-nums text-muted-foreground">
            {' '}
            ({groups} {groups === 1 ? 'group' : 'groups'})
          </span>
        )}
      </span>
      <button
        type="button"
        aria-label={`Stop grouping by ${label}`}
        onClick={onRemove}
        className="rounded-full p-0.5 text-muted-foreground outline-none hover:bg-white hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </span>
  );
}
